import { Link } from '@tanstack/react-router';
import { Plus, Youtube, ArrowLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { useYoutubeVideos, type YoutubeVideoWithExercise } from '@/lib/admin/youtube-queries';
import {
  VIDEO_STATUS_LABEL,
  VIDEO_STATUS_CLS,
  videoIsProcessing,
} from '@/lib/admin/youtube-labels';
import { DURATION_OPTIONS } from '@jeraisonne/db/schemas/youtube';

export function TutorAdminYoutubePage() {
  const { data: videos, isLoading } = useYoutubeVideos();

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6">
      <Link
        to="/tutor/dashboard"
        className="text-ink-500 hover:text-ink-900 mb-4 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Admin
      </Link>

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="font-display text-accent-500 text-[11px] font-semibold uppercase tracking-[.14em]">
            YouTube Studio
          </p>
          <h1 className="font-display text-ink-900 mt-1 text-2xl font-extrabold tracking-tight">
            Vidéos marketing
          </h1>
          <p className="text-ink-500 mt-1 font-sans text-sm">
            {videos?.length ?? 0} vidéo{(videos?.length ?? 0) !== 1 ? 's' : ''}
          </p>
        </div>
        <Link
          to="/tutor/admin/youtube/new"
          className="bg-accent-400 font-display hover:bg-accent-500 inline-flex h-11 w-full shrink-0 items-center justify-center gap-2 rounded-xl px-5 text-sm font-bold text-white shadow-md transition-all active:scale-[.98] sm:w-auto"
        >
          <Plus className="h-4 w-4" />
          Nouvelle vidéo
        </Link>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-ink-100 h-24 animate-pulse rounded-2xl" />
          ))}
        </div>
      ) : (videos?.length ?? 0) === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-3">
          {(videos ?? []).map((v) => (
            <VideoCard key={v.id} video={v} />
          ))}
        </div>
      )}
    </div>
  );
}

function durationLabel(seconds: number): string {
  return DURATION_OPTIONS.find((o) => o.value === seconds)?.label ?? `${seconds}s`;
}

function VideoCard({ video }: { video: YoutubeVideoWithExercise }) {
  return (
    <Link
      to="/tutor/admin/youtube/$videoId"
      params={{ videoId: video.id }}
      className="border-ink-200 hover:border-primary-200 block rounded-2xl border bg-white p-4 transition-all hover:shadow-md active:scale-[.99]"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={cn(
            'font-display inline-flex h-6 items-center rounded-full border px-2.5 text-[11px] font-bold',
            VIDEO_STATUS_CLS[video.status],
          )}
        >
          {VIDEO_STATUS_LABEL[video.status]}
        </span>
        <span className="text-ink-500 font-mono text-[11px]">
          {durationLabel(video.duration_seconds)}
        </span>
        <span className="text-ink-300">·</span>
        <span className="text-ink-500 font-mono text-[11px]">
          {video.aspect_ratio === 'portrait' ? '9:16' : '16:9'}
        </span>
        <ChevronRight className="text-ink-300 ml-auto h-4 w-4" />
      </div>

      <h3 className="font-display text-ink-900 mt-2 line-clamp-1 text-[15px] font-extrabold">
        {video.exercise?.title ?? 'Exercice supprimé'}
      </h3>

      {videoIsProcessing(video.status) && (
        <div className="bg-ink-100 mt-2 h-1.5 w-full overflow-hidden rounded-full">
          <div className="bg-primary-500 h-full w-1/2 animate-pulse rounded-full" />
        </div>
      )}

      <div className="border-ink-100 mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t pt-2.5">
        <span className="text-ink-400 font-sans text-xs">
          {new Date(video.created_at).toLocaleDateString('fr-FR')}
        </span>
        {video.status === 'published' && (
          <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600">
            <ExternalLink className="h-3 w-3" /> En ligne
          </span>
        )}
      </div>
    </Link>
  );
}

function EmptyState() {
  return (
    <div className="border-ink-200 flex flex-col items-center rounded-2xl border border-dashed bg-white p-10 text-center">
      <Youtube className="text-ink-300 h-8 w-8" />
      <p className="font-display text-ink-700 mt-3 text-sm font-bold">Aucune vidéo</p>
      <p className="text-ink-500 mt-1 font-sans text-xs">
        Génère une vidéo lofi / study-with-me à partir d'un exercice.
      </p>
      <Link
        to="/tutor/admin/youtube/new"
        className="bg-accent-400 font-display hover:bg-accent-500 mt-4 inline-flex h-11 items-center gap-2 rounded-xl px-5 text-sm font-bold text-white transition-all active:scale-[.98]"
      >
        <Plus className="h-4 w-4" />
        Nouvelle vidéo
      </Link>
    </div>
  );
}
